import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { SERVICES, IMAGES } from "@/lib/constants";

const accents = [
  IMAGES.crystal,
  IMAGES.cards,
  IMAGES.water,
  IMAGES.candle,
  IMAGES.smoke,
  IMAGES.hands,
];

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace("#", ""));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }, [hash]);

  return (
    <div data-testid="page-services" className="bg-cream-50">
      {/* Header */}
      <section className="relative overflow-hidden pt-8 md:pt-12 pb-14 md:pb-20">
        <div className="aura-bg opacity-50" />
        <div className="relative page-shell grid grid-cols-12 gap-8 md:gap-10">
          <div className="col-span-12 md:col-span-9 min-w-0">
            <div className="eyebrow mb-6">Hizmetler</div>
            <h1 className="max-w-full md:max-w-none font-display text-[16vw] sm:text-[13vw] md:text-[8vw] lg:text-[7.5rem] font-light leading-[0.95] tracking-normal text-midnight">
              Altı <em className="text-lavender-700">kapı</em>,
              <br />
              tek bir <em className="text-gold-soft italic">yolculuk</em>.
            </h1>
          </div>
          <div className="col-span-12 md:col-span-3 md:pt-10">
            <p className="w-full max-w-full md:max-w-none text-lavender-700 leading-relaxed text-[15px]">
              Her hizmet bireysel ve online formatlarda sunulur. Önce kısa bir ön
              görüşme yapar; sonra sana en uygun çalışmayı birlikte belirleriz.
            </p>
          </div>
        </div>
      </section>

      {/* Services list — editorial alternating */}
      <section className="pb-20" data-testid="services-list">
        <div className="page-shell divide-y divide-cream-300 border-y border-cream-300">
          {SERVICES.map((s, i) => {
            const reversed = i % 2 === 1;
            return (
              <article
                key={s.slug}
                id={s.slug}
                data-testid={`service-${s.slug}`}
                className="grid grid-cols-12 gap-6 md:gap-10 py-12 md:py-20 items-start scroll-mt-28 md:scroll-mt-32"
              >
                <div
                  className={`col-span-12 md:col-span-1 ${
                    reversed ? "md:order-3" : ""
                  }`}
                >
                  <span className="number-marker text-2xl">0{i + 1}</span>
                </div>
                <div
                  className={`col-span-12 md:col-span-4 ${
                    reversed ? "md:order-1" : ""
                  }`}
                >
                  <div className="img-zoom rounded-sm aspect-[4/5]">
                    <img src={accents[i % accents.length]} alt={s.title} />
                  </div>
                </div>
                <div
                  className={`col-span-12 md:col-span-7 ${
                    reversed ? "md:order-2" : ""
                  }`}
                >
                  <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-light text-midnight leading-[1.02] tracking-normal">
                    {s.title}
                  </h2>
                  <p className="text-lavender-700 mt-6 leading-relaxed text-[15px] max-w-2xl">
                    {s.description}
                  </p>

                  <div className="mt-8 md:mt-10 grid grid-cols-1 md:grid-cols-2 gap-7 md:gap-8">
                    <div>
                      <div className="text-[10px] tracking-[0.35em] uppercase text-gold-deep mb-4">
                        Kimler için uygundur
                      </div>
                      <ul className="space-y-3">
                        {s.suitableFor.map((p) => (
                          <li
                            key={p}
                            className="flex items-start gap-3 text-midnight text-[14px] leading-relaxed"
                          >
                            <Check
                              size={14}
                              className="text-gold mt-1 shrink-0"
                            />
                            {p}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div className="space-y-6">
                      <div>
                        <div className="text-[10px] tracking-[0.35em] uppercase text-gold-deep mb-2">
                          Süre
                        </div>
                        <div className="font-display text-2xl text-midnight">
                          {s.duration}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] tracking-[0.35em] uppercase text-gold-deep mb-2">
                          Format
                        </div>
                        <div className="font-display text-xl text-midnight">
                          {s.format}
                        </div>
                      </div>
                    </div>
                  </div>

                  <Link
                    to={`/randevu?hizmet=${s.slug}`}
                    data-testid={`service-cta-${s.slug}`}
                    className="btn-midnight mt-10"
                  >
                    Randevu Al <ArrowUpRight size={16} />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* FAQ-ish */}
      <section className="py-20 md:py-32 bg-lavender-50/50" data-testid="services-faq">
        <div className="page-shell grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <div className="eyebrow mb-6">Sıkça Sorulanlar</div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-light text-midnight leading-[1.05]">
              Merak ettiklerin,
              <br />
              <em className="text-gold-soft">netleşsin</em>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6">
            {[
              {
                q: "Seanslar online mı, yüz yüze mi?",
                a: "Bireysel seanslar İstanbul'daki atölyemde yüz yüze; ulaşılamayan danışanlar için tüm hizmetler Zoom üzerinden online verilir.",
              },
              {
                q: "İlk seansta ne yaşarım?",
                a: "Kısa bir tanışma ardından ihtiyacının haritasını birlikte çıkarırız. Sonraki adımlar tamamen sana özel belirlenir.",
              },
              {
                q: "Birkaç seansa ihtiyacım olur mu?",
                a: "Bazı çalışmalar tek seansta tamamlanır (rüya analizi, numeroloji); soy karması ve akış terapisi genelde 3-5 seans önerilir.",
              },
              {
                q: "Randevuyu nasıl alıyorum?",
                a: "Randevu sayfasından formu doldurman yeterli — formun sonunda WhatsApp üzerinden teyit alıyoruz.",
              },
            ].map((f, i) => (
              <details
                key={i}
                data-testid={`faq-${i}`}
                className="group border-b border-cream-300 py-6"
              >
                <summary className="flex items-start justify-between gap-4 cursor-pointer list-none">
                  <span className="font-display text-2xl md:text-3xl text-midnight">
                    {f.q}
                  </span>
                  <span className="text-gold group-open:rotate-45 transition-transform duration-300 text-2xl font-light">
                    +
                  </span>
                </summary>
                <p className="text-lavender-700 mt-4 leading-relaxed md:pr-12">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
